<template>
  <div class="relative flex flex-col">
    <div
      class="sticky top-0 z-10 flex flex-wrap space-x-2 bg-gray-50 px-1 py-1"
      v-if="editor"
      :class="{
        'rounded-t border': !inline,
        'border-b': inline,
      }"
    >
      <template v-for="({ separator, filler, buttons }, idx) in buttonGroups" :key="idx">
        <div class="w-[1px] bg-gray-300" v-if="separator"></div>
        <div class="flex-1" v-else-if="filler"></div>
        <div class="flex h-9 -space-x-1" v-else-if="buttons">
          <button
            v-for="{ icon, enabled, action, command, isActive, mobile } in buttons"
            type="button"
            class="flex items-center justify-center px-3"
            :class="{
              'sm:hidden': mobile,
              'rounded-sm text-gray-700 hover:bg-gray-200 hover:text-gray-900':
                enabled(editor) && !isActive(editor),
              'rounded-sm text-brand-500 hover:bg-brand-100 hover:text-brand-600':
                enabled(editor) && isActive(editor),
              'cursor-default text-gray-300': !enabled(editor),
            }"
            @click="performButtonAction(action, command)"
          >
            <span class="fas" :class="icon" />
          </button>
        </div>
      </template>
    </div>
    <editor-content
      :editor="editor"
      class="p-2"
      :class="{
        'border border-t-0': !inline,
      }"
    />
  </div>
</template>

<script>
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import { Link } from '@tiptap/extension-link'
import { ref, watch } from 'vue'
import { Placeholder } from '../classes/Placeholder.ts'

export default {
  components: {
    EditorContent,
  },

  props: {
    modelValue: {
      type: [Object, String],
      default: '',
    },
    ignorePlaceholderValues: {
      type: Boolean,
      default: false,
    },
    highlightEmptyPlaceholders: {
      type: Boolean,
      default: false,
    },
    inline: {
      type: Boolean,
      default: false,
    },
  },

  setup({ modelValue, ignorePlaceholderValues, highlightEmptyPlaceholders }, { emit }) {
    const linkAction = (editor) => {
      const { view, state } = editor.value
      const { from, to } = view.state.selection
      const text = state.doc.textBetween(from, to, ' ')

      const textIsPhoneNumber = /(\+?[\d\-.]+){8,15}/.test(text)

      const previousUrl =
        editor.value.getAttributes('link').href || (textIsPhoneNumber ? 'tel:' + text : '')
      const url = window.prompt('URL', previousUrl)

      // cancelled
      if (url === null) {
        return
      }

      // empty
      if (url === '') {
        return editor.value.chain().focus().extendMarkRange('link').unsetLink()
      }

      // update link
      return editor.value.chain().focus().extendMarkRange('link').setLink({ href: url })
    }

    const editor = useEditor({
      content: modelValue,
      extensions: [
        StarterKit.configure({
          heading: {
            levels: [1, 2, 3],
          },
        }),
        Link.extend({
          addKeyboardShortcuts() {
            return {
              // ↓ your new keyboard shortcut
              'Mod-k': () => {
                linkAction(editor)?.run()
              },
            }
          },
        }).configure({
          openOnClick: false,
        }),
        Placeholder.configure({
          ignoreValues: ignorePlaceholderValues,
          highlightEmpty: highlightEmptyPlaceholders,
        }),
      ],
      editorProps: {
        attributes: {
          class: 'prose prose-hr:my-6 max-w-none m-3 focus:outline-none leading-relaxed',
        },
      },
      onUpdate: ({ editor }) => {
        emit('update:modelValue', editor.getJSON())
      },
    })

    watch(
      () => modelValue,
      () => {
        const isSame = JSON.stringify(editor.value.getJSON()) === JSON.stringify(value)

        if (isSame) {
          return
        }

        editor.value.commands.setContent(value, false)
      }
    )

    const buttonGroups = [
      {
        buttons: [
          {
            icon: 'fa-bold',
            action: (modifier) => modifier.toggleBold(),
            isActive: (editor) => editor.isActive('bold'),
            enabled: (editor) => editor.can().toggleBold(),
          },
          {
            icon: 'fa-italic',
            action: (modifier) => modifier.toggleItalic(),
            isActive: (editor) => editor.isActive('italic'),
            enabled: (editor) => editor.can().toggleItalic(),
          },
        ],
      },
      { separator: true },
      {
        buttons: [
          {
            icon: 'fa-link',
            action: (action, editor) => linkAction(editor),
            isActive: (editor) => !!editor.getAttributes('link').href,
            enabled: (editor) => editor.can().setLink(),
          },
        ],
      },
      { separator: true },
      {
        buttons: [
          {
            icon: 'fa-h1',
            action: (action) => action.toggleHeading({ level: 1 }),
            isActive: (editor) => editor.isActive('heading', { level: 1 }),
            enabled: (editor) => editor.can().toggleHeading({ level: 1 }),
          },
          {
            icon: 'fa-h2',
            action: (action) => action.toggleHeading({ level: 2 }),
            isActive: (editor) => editor.isActive('heading', { level: 2 }),
            enabled: (editor) => editor.can().toggleHeading({ level: 2 }),
          },
          {
            icon: 'fa-h3',
            action: (action) => action.toggleHeading({ level: 3 }),
            isActive: (editor) => editor.isActive('heading', { level: 3 }),
            enabled: (editor) => editor.can().toggleHeading({ level: 3 }),
          },
        ],
      },
      { separator: true },
      {
        buttons: [
          {
            icon: 'fa-horizontal-rule',
            action: (action) => action.setHorizontalRule(),
            isActive: () => false,
            enabled: (editor) => editor.can().setHorizontalRule(),
          },
          {
            icon: 'fa-level-down',
            action: (action) => action.setHardBreak(),
            isActive: () => false,
            enabled: (editor) => editor.can().setHardBreak(),
            mobile: true,
          },
          {
            icon: 'fa-list-ul',
            action: (action) => action.toggleBulletList(),
            isActive: (editor) => editor.isActive('bulletList'),
            enabled: (editor) => editor.can().toggleBulletList(),
          },
          {
            icon: 'fa-list-ol',
            action: (action) => action.toggleOrderedList(),
            isActive: (editor) => editor.isActive('orderedList'),
            enabled: (editor) => editor.can().toggleOrderedList(),
          },
          {
            icon: 'fa-indent',
            action: (action) => action.sinkListItem('listItem'),
            isActive: () => false,
            enabled: (editor) => editor.can().sinkListItem('listItem'),
          },
          {
            icon: 'fa-outdent',
            action: (action) => action.liftListItem('listItem'),
            isActive: () => false,
            enabled: (editor) => editor.can().liftListItem('listItem'),
          },
        ],
      },
      { filler: true },
      {
        buttons: [
          {
            icon: 'fa-undo',
            action: (modifier) => modifier.undo(),
            isActive: () => false,
            enabled: (editor) => editor.can().undo(),
          },
          {
            icon: 'fa-redo',
            action: (modifier) => modifier.redo(),
            isActive: () => false,
            enabled: (editor) => editor.can().redo(),
          },
        ],
      },
    ]

    const performButtonAction = (action, command) => {
      if (action) {
        action(editor.value.chain().focus(), editor)?.run()
      }

      if (command) {
        command(editor.value.commands, editor)
      }
    }

    const updateValue = (value) => {
      editor.value.commands.setContent(value, false)
    }

    const addPlaceholder = (provider, item, label) => {
      const emptySpaceText = {
        type: 'text',
        text: ' ',
      }

      const content = [
        {
          type: 'placeholder',
          attrs: {
            provider,
            item,
            label,
            value: null,
          },
        },
      ]

      const { state } = editor.value
      const { from } = state.selection
      const { doc } = state

      let isPreviousNodePlaceholderNode = false

      doc.descendants((node, pos) => {
        if (isPreviousNodePlaceholderNode) return false
        if (node.type.name === 'placeholder' && from - 2 === pos) {
          isPreviousNodePlaceholderNode = true
        }
      })

      if (isPreviousNodePlaceholderNode) content.unshift(emptySpaceText)

      editor.value?.chain().focus().insertContent(content).run()
    }

    return {
      editor,
      buttonGroups,
      performButtonAction,
      updateValue,
      addPlaceholder,
      focus() {
        editor.value?.chain().focus().run()
      },
    }
  },
}
</script>
